import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type { TemplateDef, FormValues, FieldDef, SectionDef } from '../types';
import FormSection from './FormSection';
import { validateDates } from '../../shared/utils/dateUtils';
import AgentChatWidget from './AgentChatWidget';
import { createBriefingPromocao, validateBriefingProducts } from '../services/briefingPromocoesApi';
import {
  normalizeProductCode,
  parseProductCodes,
  shouldValidateProducts,
} from './ProductValidationPreview';
import {
  exportRebaixaSimples,
  exportRebaixaProgressiva,
  exportCompreEGanhe,
  exportListaSugeridaOriginal,
  exportListaSugeridaRecompra,
  exportCupomSimples,
  exportCupomProgressivo,
  exportCashback,
  exportRebaixaCupomGeral,
} from '../../shared/utils/xlsxExport';
import {
  getSellerOptionDisplayValue,
  getSellerOptionsFromTemplate,
  isSellerFieldKey,
  normalizeSellerFieldsInValues,
  normalizeSellerValue,
} from '../../shared/utils/sellerValues';

interface PromotionFormProps {
  template: TemplateDef;
  onReset: () => void;
  initialValues?: FormValues;
  reuseSourceId?: string | number;
}

interface ProductFieldValidationTarget {
  fieldKey: string;
  label: string;
  codes: string[];
}

interface ProductSaveValidationIssue {
  fieldKey: string;
  label: string;
  missing: string[];
  remaining: string[];
}

interface PendingProductConfirmation {
  values: FormValues;
  issues: ProductSaveValidationIssue[];
}

export type RenderedSection = SectionDef & {
  childNumber?: number;
};

export interface ChildSectionGroup {
  childNumber: number;
  sections: RenderedSection[];
}

export interface RenderedFormLayout {
  baseSections: RenderedSection[];
  childGroups: ChildSectionGroup[];
}

export interface SectionRenderSlot {
  section: RenderedSection;
  sectionIndex: number;
  displayIndex: number;
}

export type FormRenderItem =
  | {
      kind: 'section';
      key: string;
      slot: SectionRenderSlot;
    }
  | {
      kind: 'childGroup';
      key: string;
      childNumber: number;
      color: string;
      slots: SectionRenderSlot[];
    };

const HISTORY_ROUTE = '/briefing-promocoes/historico';
const DEFAULT_FORM_VALUES: FormValues = {
  tipoSolicitacao: '',
  idIFC: 'n/a',
};

const TEMPLATE_DEFAULT_VALUES: Record<string, FormValues> = {
  t4a: { tipoLista: '1' },
  t4b: { tipoLista: '2' },
};

const XLSX_EXPORTERS: Record<string, (values: FormValues) => void> = {
  t1: exportRebaixaSimples,
  t2: exportRebaixaProgressiva,
  t3: exportCompreEGanhe,
  t4a: exportListaSugeridaOriginal,
  t4b: exportListaSugeridaRecompra,
  t5: exportCupomSimples,
  t6: exportCupomProgressivo,
  t7: exportCashback,
  t8: exportRebaixaCupomGeral,
};

const CHILD_COUNT_KEY = 'qtdCardsFilhos';
const CHILD_MAX_COUNT = 15;
const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;

const childCountField: FieldDef = {
  key: CHILD_COUNT_KEY,
  label: 'Quantidade de cards filhos',
  type: 'number',
  placeholder: '0 a 15',
  helperText: 'Ao informar filhos, serão exibidos blocos adicionais.',
  conditional: { field: 'numeroCard', values: ['1'] },
};

const CHILD_REPEAT_SECTIONS_BY_TEMPLATE: Record<string, string[]> = {
  t3: ['Cards e Desconto', 'Gatilho da Família', 'Configuração da Família'],
  t4a: ['Cards e Estrutura', 'Configuração da Família'],
  t4b: ['Cards e Estrutura', 'Configuração da Família'],
};
const CARD_CONTROLLER_SECTION_TITLES = ['Cards e Desconto', 'Cards e Estrutura'];
const CHILD_GROUP_COLORS = ['#1565c0', '#2e7d32', '#ef6c00', '#6a1b9a'];

function normalizeChildCount(value: unknown): number {
  if (Array.isArray(value)) return 0;
  const parsed = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(CHILD_MAX_COUNT, parsed));
}

function childFieldKey(fieldKey: string, childNumber: number): string {
  return `${fieldKey}__child${childNumber}`;
}

function childNumberFromKey(fieldKey: string): number | null {
  const match = fieldKey.match(CHILD_FIELD_SUFFIX_RE);
  if (!match) return null;
  const parsed = parseInt(match[1], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function baseFieldKey(fieldKey: string): string {
  return fieldKey.replace(CHILD_FIELD_SUFFIX_RE, '');
}

function relatedFieldKey(fieldKey: string, relatedBaseKey: string): string {
  const childNumber = childNumberFromKey(fieldKey);
  return childNumber === null ? relatedBaseKey : childFieldKey(relatedBaseKey, childNumber);
}

function removeChildValues(values: FormValues) {
  Object.keys(values).forEach((key) => {
    if (key === CHILD_COUNT_KEY || childNumberFromKey(key) !== null) {
      delete values[key];
    }
  });
}

function syncChildCardValues(values: FormValues) {
  if (values.numeroCard !== '1') {
    removeChildValues(values);
    return;
  }

  const count = normalizeChildCount(values[CHILD_COUNT_KEY]);
  values[CHILD_COUNT_KEY] = count > 0 ? String(count) : '';

  Object.keys(values).forEach((key) => {
    const childNumber = childNumberFromKey(key);
    if (childNumber !== null && (childNumber < 2 || childNumber > count + 1)) {
      delete values[key];
    }
  });

  for (let childNumber = 2; childNumber <= count + 1; childNumber += 1) {
    values[childFieldKey('numeroCard', childNumber)] = String(childNumber);
  }
}

function normalizeChangedValue(key: string, value: string | string[]): string | string[] {
  if (key === CHILD_COUNT_KEY) {
    const count = normalizeChildCount(value);
    return count > 0 ? String(count) : '';
  }

  const childNumber = childNumberFromKey(key);
  if (baseFieldKey(key) === 'numeroCard' && childNumber !== null) {
    return String(childNumber);
  }

  return value;
}

function cloneFieldForChild(field: FieldDef, childNumber: number): FieldDef {
  const helperText = field.helperText?.includes('card pai')
    ? 'Preencher para este card filho.'
    : field.helperText;

  const cloned: FieldDef = {
    ...field,
    key: childFieldKey(field.key, childNumber),
    helperText,
    conditional: field.conditional
      ? {
          ...field.conditional,
          field: childFieldKey(field.conditional.field, childNumber),
        }
      : undefined,
    excludeValuesFrom: field.excludeValuesFrom
      ? childFieldKey(field.excludeValuesFrom, childNumber)
      : undefined,
  };

  if (field.key === 'numeroCard') {
    const fixedOption = field.options?.find((option) => option.value === String(childNumber));
    cloned.options = [
      fixedOption ?? {
        value: String(childNumber),
        label: `${childNumber} - Card filho ${childNumber}`,
      },
    ];
    cloned.helperText = 'Card filho definido automaticamente por este bloco.';
  }

  return cloned;
}

function findSectionByTitle(sections: SectionDef[], title: string): SectionDef | undefined {
  return sections.find((section) => section.title === title);
}

function getChildRepeatSectionTitles(templateId: string): string[] {
  return CHILD_REPEAT_SECTIONS_BY_TEMPLATE[templateId] ?? [];
}

function hasCardNumberField(section: SectionDef): boolean {
  return section.fields.some((field) => field.key === 'numeroCard');
}

function getCardsSection(templateId: string, sections: SectionDef[]): SectionDef | undefined {
  const cardSectionTitle = getChildRepeatSectionTitles(templateId)[0];
  if (!cardSectionTitle) return undefined;

  const configuredSection = findSectionByTitle(sections, cardSectionTitle);
  if (configuredSection && hasCardNumberField(configuredSection)) return configuredSection;

  return sections.find(
    (section) => CARD_CONTROLLER_SECTION_TITLES.includes(section.title) && hasCardNumberField(section),
  );
}

function getChildRepeatSections(templateId: string, sections: SectionDef[]): SectionDef[] {
  const repeatSectionTitles = new Set(getChildRepeatSectionTitles(templateId));
  const cardsSection = getCardsSection(templateId, sections);
  if (!cardsSection) return [];
  repeatSectionTitles.add(cardsSection.title);

  return sections.filter((section) => repeatSectionTitles.has(section.title));
}

function getChildGroupColor(childNumber: number): string {
  return CHILD_GROUP_COLORS[(childNumber - 2) % CHILD_GROUP_COLORS.length];
}

export function buildRenderedLayout(templateId: string, sections: SectionDef[], values: FormValues): RenderedFormLayout {
  const repeatSections = getChildRepeatSections(templateId, sections);
  const cardsSection = getCardsSection(templateId, sections);
  if (repeatSections.length === 0) {
    return {
      baseSections: sections,
      childGroups: [],
    };
  }

  const baseSections = sections.map((section) => {
    if (section.title !== cardsSection?.title) return section;
    if (section.fields.some((field) => field.key === CHILD_COUNT_KEY)) return section;

    return {
      ...section,
      fields: [...section.fields, childCountField],
    };
  });

  const childCount = values.numeroCard === '1' ? normalizeChildCount(values[CHILD_COUNT_KEY]) : 0;
  if (childCount <= 0) {
    return {
      baseSections,
      childGroups: [],
    };
  }

  const childGroups: ChildSectionGroup[] = [];

  for (let childNumber = 2; childNumber <= childCount + 1; childNumber += 1) {
    childGroups.push({
      childNumber,
      sections: repeatSections.map((section) => ({
        ...section,
        childNumber,
        fields: section.fields.map((field) => cloneFieldForChild(field, childNumber)),
      })),
    });
  }

  return {
    baseSections,
    childGroups,
  };
}

export function getRenderedSectionCount(layout: RenderedFormLayout): number {
  return layout.baseSections.length + layout.childGroups.reduce((total, group) => total + group.sections.length, 0);
}

export function buildRenderItems(layout: RenderedFormLayout): FormRenderItem[] {
  const items: FormRenderItem[] = [];
  let sectionIndex = 0;

  layout.baseSections.forEach((section, displayIndex) => {
    items.push({
      kind: 'section',
      key: `section-${section.title}-${sectionIndex}`,
      slot: {
        section,
        sectionIndex,
        displayIndex,
      },
    });
    sectionIndex += 1;
  });

  layout.childGroups.forEach((group) => {
    const slots = group.sections.map((section, displayIndex) => {
      const slot = {
        section,
        sectionIndex,
        displayIndex,
      };
      sectionIndex += 1;
      return slot;
    });

    items.push({
      kind: 'childGroup',
      key: `child-group-${group.childNumber}`,
      childNumber: group.childNumber,
      color: getChildGroupColor(group.childNumber),
      slots,
    });
  });

  return items;
}

function isFieldVisibleForValues(field: FieldDef, values: FormValues): boolean {
  if (!field.conditional) return true;

  const condValue = values[field.conditional.field];
  return Array.isArray(condValue)
    ? condValue.some((value) => field.conditional!.values.includes(String(value)))
    : field.conditional.values.includes(String(condValue ?? ''));
}

function isEmptyRequiredValue(value: FormValues[string] | undefined): boolean {
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => !String(item ?? '').trim());
  }

  return !String(value ?? '').trim();
}

function isEmptyLegacyIfcValue(value: FormValues[string] | undefined): boolean {
  if (Array.isArray(value)) return value.length === 0;

  const normalized = String(value ?? '').trim().toLowerCase();

  return !normalized || ['n/a', 'na', 'não se aplica', 'nao se aplica'].includes(normalized);
}

function isBenefitFamilyType(value: FormValues[string] | undefined): boolean {
  return ['2', '3'].includes(String(value ?? '').trim());
}

function getRenderSlots(renderItems: FormRenderItem[]): SectionRenderSlot[] {
  return renderItems.flatMap((item) => (item.kind === 'section' ? [item.slot] : item.slots));
}

function validateRequiredFields(renderItems: FormRenderItem[], values: FormValues, templateId: string): Record<string, string> {
  const errors: Record<string, string> = {};

  getRenderSlots(renderItems).forEach(({ section }) => {
    section.fields.forEach((field) => {
      if (!field.required || field.type === 'info') return;
      if (!isFieldVisibleForValues(field, values)) return;
      if (!isEmptyRequiredValue(values[field.key])) return;

      errors[field.key] = `${field.label} é obrigatório.`;
    });
  });

  if (templateId === 't3') {
    getRenderSlots(renderItems).forEach(({ section }) => {
      section.fields.forEach((field) => {
        if (baseFieldKey(field.key) !== 'tipoFamilia') return;
        if (!isFieldVisibleForValues(field, values)) return;
        if (!isBenefitFamilyType(values[field.key])) return;

        const discountKey = relatedFieldKey(field.key, 'valorDesconto');
        if (!isEmptyRequiredValue(values[discountKey])) return;

        errors[discountKey] = 'Valor do desconto é obrigatório para famílias do tipo Benefício ou Ambos.';
      });
    });
  }

  if (String(values.tipoSolicitacao ?? '') === '1' && isEmptyLegacyIfcValue(values.idIFC)) {
    errors.idIFC = 'Informe o ID Infracommerce quando o tipo de solicitação for legado.';
  }

  return errors;
}

function getFirstFieldErrorKey(renderItems: FormRenderItem[], fieldErrors: Record<string, string>): string | undefined {
  for (const { section } of getRenderSlots(renderItems)) {
    for (const field of section.fields) {
      if (fieldErrors[field.key]) return field.key;
    }
  }

  return undefined;
}

function getInvalidSectionIndexes(renderItems: FormRenderItem[], fieldErrors: Record<string, string>): Set<number> {
  const indexes = new Set<number>();

  getRenderSlots(renderItems).forEach(({ section, sectionIndex }) => {
    if (section.fields.some((field) => fieldErrors[field.key])) {
      indexes.add(sectionIndex);
    }
  });

  return indexes;
}

function scrollToField(fieldKey: string) {
  window.setTimeout(() => {
    const target = Array.from(document.querySelectorAll<HTMLElement>('[data-field-key]'))
      .find((element) => element.dataset.fieldKey === fieldKey);

    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const input = target.querySelector<HTMLElement>('input, textarea, [contenteditable="true"]');
    input?.focus({ preventScroll: true });
  }, 120);
}

function cloneFormValues(values: FormValues): FormValues {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
  ) as FormValues;
}

function valueWithoutMissingProductCodes(
  value: FormValues[string] | undefined,
  missing: string[]
): string | string[] {
  const missingSet = new Set(missing.map(normalizeProductCode));
  const remaining = parseProductCodes(value).filter((code) => !missingSet.has(normalizeProductCode(code)));

  return Array.isArray(value) ? remaining : remaining.join(',');
}

function collectProductValidationTargets(renderItems: FormRenderItem[], values: FormValues): ProductFieldValidationTarget[] {
  const targets = new Map<string, ProductFieldValidationTarget>();

  getRenderSlots(renderItems).forEach(({ section }) => {
    section.fields.forEach((field) => {
      if (!isFieldVisibleForValues(field, values)) return;
      if (!shouldValidateProducts(field.key, values)) return;

      const codes = parseProductCodes(values[field.key]);
      if (codes.length === 0) return;

      const label = section.childNumber
        ? `${field.label} - Card filho ${section.childNumber}`
        : field.label;

      targets.set(field.key, {
        fieldKey: field.key,
        label,
        codes,
      });
    });
  });

  return Array.from(targets.values());
}

async function validateProductsBeforeSave(
  renderItems: FormRenderItem[],
  values: FormValues
): Promise<PendingProductConfirmation | null> {
  const targets = collectProductValidationTargets(renderItems, values);
  const allCodes = Array.from(new Set(targets.flatMap((target) => target.codes).map(normalizeProductCode))).filter(Boolean);

  if (allCodes.length === 0) return null;

  const result = await validateBriefingProducts(allCodes);
  if (result.missing.length === 0) return null;

  const missingSet = new Set(result.missing.map(normalizeProductCode));
  const cleanedValues = cloneFormValues(values);
  const issues: ProductSaveValidationIssue[] = [];

  targets.forEach((target) => {
    const missing = target.codes.filter((code) => missingSet.has(normalizeProductCode(code)));
    if (missing.length === 0) return;

    const cleanedValue = valueWithoutMissingProductCodes(cleanedValues[target.fieldKey], missing);
    cleanedValues[target.fieldKey] = cleanedValue;

    issues.push({
      fieldKey: target.fieldKey,
      label: target.label,
      missing,
      remaining: parseProductCodes(cleanedValue),
    });
  });

  return issues.length > 0 ? { values: cleanedValues, issues } : null;
}

function getInitialFormValues(templateId: string, initialValues?: FormValues): FormValues {
  const values = {
    ...DEFAULT_FORM_VALUES,
    ...(TEMPLATE_DEFAULT_VALUES[templateId] ?? {}),
    ...(initialValues ? cloneFormValues(initialValues) : {}),
  };

  syncChildCardValues(values);

  return values;
}

export default function PromotionForm({ template, onReset, initialValues, reuseSourceId }: PromotionFormProps) {
  const navigate = useNavigate();
  const [values, setValues] = useState<FormValues>(() => getInitialFormValues(template.id, initialValues));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [savedId, setSavedId] = useState<string | number | undefined>(undefined);
  const [pendingProductConfirmation, setPendingProductConfirmation] = useState<PendingProductConfirmation | null>(null);
  const [expandedSections, setExpandedSections] = useState<boolean[]>(
    () => template.sections.map(() => true),
  );

  const renderedLayout = useMemo(
    () => buildRenderedLayout(template.id, template.sections, values),
    [template.id, template.sections, values]
  );
  const sellerOptions = useMemo(() => getSellerOptionsFromTemplate(template), [template]);
  const renderItems = useMemo(() => buildRenderItems(renderedLayout), [renderedLayout]);
  const renderedSectionCount = useMemo(() => getRenderedSectionCount(renderedLayout), [renderedLayout]);

  const allExpanded = renderedSectionCount > 0 && Array.from({ length: renderedSectionCount }, (_, idx) => expandedSections[idx] ?? true).every(Boolean);
  const allCollapsed = renderedSectionCount > 0 && Array.from({ length: renderedSectionCount }, (_, idx) => !(expandedSections[idx] ?? true)).every(Boolean);

  useEffect(() => {
    const nextValues = getInitialFormValues(template.id, initialValues);

    setValues(nextValues);
    setSubmitted(false);
    setSavedId(undefined);
    setSaveError(null);
    setValidationErrors({});
    setPendingProductConfirmation(null);
    setExpandedSections(Array.from({ length: getRenderedSectionCount(buildRenderedLayout(template.id, template.sections, nextValues)) }, () => true));
  }, [template.id, initialValues]);

  useEffect(() => {
    setExpandedSections((prev) => Array.from({ length: renderedSectionCount }, (_, idx) => prev[idx] ?? true));
  }, [renderedSectionCount]);

  const handleChange = (key: string, value: string | string[]) => {
    setValues((prev) => {
      const normalizedValue = normalizeChangedValue(key, value);
      const next = { ...prev, [key]: normalizedValue };

      if (key === 'nomeExibicao' && typeof value === 'string') {
        const previousDisplayName = typeof prev.nomeExibicao === 'string' ? prev.nomeExibicao : '';
        const currentDescription = typeof prev.descricao === 'string' ? prev.descricao : '';
        const currentInternalDescription = typeof prev.descricaoInterna === 'string' ? prev.descricaoInterna : '';

        if (!currentDescription.trim() || currentDescription === previousDisplayName) {
          next.descricao = value;
        }

        if (!currentInternalDescription.trim() || currentInternalDescription === previousDisplayName) {
          next.descricaoInterna = value;
        }
      }

      if (key === 'tipoSolicitacao' && typeof value === 'string') {
        if (value === '1' && isEmptyLegacyIfcValue(prev.idIFC)) {
          next.idIFC = '';
        }

        if (value !== '1') {
          next.idIFC = 'n/a';
        }
      }

      syncChildCardValues(next);

      return next;
    });
    setValidationErrors((prev) => {
      const shouldClearRelatedDiscount = baseFieldKey(key) === 'tipoFamilia';
      if (!prev[key] && key !== 'tipoSolicitacao' && !shouldClearRelatedDiscount) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === 'tipoSolicitacao') delete next.idIFC;
      if (shouldClearRelatedDiscount) delete next[relatedFieldKey(key, 'valorDesconto')];
      return next;
    });
    if (submitted) setSubmitted(false);
    if (saveError) setSaveError(null);
    if (pendingProductConfirmation) setPendingProductConfirmation(null);
  };

  const handleToggleSection = (idx: number) => {
    setExpandedSections((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const handleExpandAll = () => setExpandedSections(Array.from({ length: renderedSectionCount }, () => true));
  const handleCollapseAll = () => setExpandedSections(Array.from({ length: renderedSectionCount }, () => false));

  const dateErrors = useMemo(() => {
    const start = (values['inicioVigencia'] as string) ?? '';
    const end = (values['fimVigencia'] as string) ?? '';
    const { startError, endError } = validateDates(start, end);
    const errors: Record<string, string> = {};
    if (startError) errors['inicioVigencia'] = startError;
    if (endError) errors['fimVigencia'] = endError;
    return errors;
  }, [values]);

  const fieldErrors = useMemo(
    () => ({
      ...validationErrors,
      ...dateErrors,
    }),
    [validationErrors, dateErrors]
  );

  const submitValues = async (
    valuesToSubmit: FormValues,
    options: { skipProductValidationWarning?: boolean } = {}
  ) => {
    setSubmitted(false);
    setSaveError(null);
    setPendingProductConfirmation(null);

    const requiredErrors = validateRequiredFields(renderItems, valuesToSubmit, template.id);
    const nextFieldErrors = {
      ...requiredErrors,
      ...dateErrors,
    };
    const firstErrorKey = getFirstFieldErrorKey(renderItems, nextFieldErrors);

    if (firstErrorKey) {
      setValidationErrors(requiredErrors);
      const invalidSectionIndexes = getInvalidSectionIndexes(renderItems, nextFieldErrors);
      setExpandedSections((prev) =>
        Array.from({ length: renderedSectionCount }, (_, idx) =>
          invalidSectionIndexes.has(idx) ? true : (prev[idx] ?? true)
        )
      );
      setSaveError('Preencha os campos obrigatórios destacados antes de salvar o briefing.');
      scrollToField(firstErrorKey);
      return;
    }

    setValidationErrors({});

    try {
      setSaving(true);

      if (!options.skipProductValidationWarning) {
        const productConfirmation = await validateProductsBeforeSave(renderItems, valuesToSubmit);

        if (productConfirmation) {
          setPendingProductConfirmation(productConfirmation);
          scrollToField(productConfirmation.issues[0].fieldKey);
          return;
        }
      }

      const valuesToSave = normalizeSellerFieldsInValues(valuesToSubmit, sellerOptions);
      const id = await createBriefingPromocao(template.id, valuesToSave);
      setSavedId(id);
      setSubmitted(true);

      if (id) {
        navigate(`${HISTORY_ROUTE}?briefingId=${id}`, {
          state: {
            selectedBriefingId: String(id),
            openBriefing: true,
            created: true,
          },
        });
      }
    } catch (error) {
      console.log(error);
      setSaveError(error instanceof Error ? error.message : 'Não foi possível salvar o briefing.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    void submitValues(values);
  };

  const handleConfirmSaveWithoutInvalidProducts = () => {
    if (!pendingProductConfirmation) return;

    const cleanedValues = pendingProductConfirmation.values;
    setPendingProductConfirmation(null);
    setValues(cleanedValues);
    void submitValues(cleanedValues, { skipProductValidationWarning: true });
  };

  const handleCancelProductConfirmation = () => {
    setPendingProductConfirmation(null);
  };

  const handleCopyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(values, null, 2));
  };

  const handleClearForm = () => {
    setValues(getInitialFormValues(template.id));
    setSubmitted(false);
    setSavedId(undefined);
    setSaveError(null);
    setValidationErrors({});
    setPendingProductConfirmation(null);
  };

  function normalizeText(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function splitAgentListValue(rawValue: unknown): string[] {
  if (Array.isArray(rawValue)) {
    return rawValue.flatMap(splitAgentListValue);
  }

  return String(rawValue ?? '')
    .split(/[,;\n]+|\s+e\s+/i)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getOptionNameAliases(option: { value: string; label: string }): string[] {
  const normalizedLabel = normalizeText(option.label);
  const normalizedValue = normalizeText(option.value);
  const withoutValuePrefix = normalizedValue && normalizedLabel.startsWith(`${normalizedValue} `)
    ? normalizedLabel.slice(normalizedValue.length).trim()
    : normalizedLabel;
  const withoutTrailingUf = withoutValuePrefix.replace(/\b(ac|al|ap|am|ba|ce|df|es|go|ma|mt|ms|mg|pa|pb|pr|pe|pi|rj|rn|rs|ro|rr|sc|sp|se|to)\b$/i, '').trim();

  return Array.from(new Set([
    normalizedLabel,
    withoutValuePrefix,
    withoutTrailingUf,
    normalizedValue,
  ].filter((item) => item.length >= 2)));
}

function getOptionMatchScore(input: string, option: { value: string; label: string }): number {
  const normalizedInput = normalizeText(input);
  if (!normalizedInput) return 0;

  const aliases = getOptionNameAliases(option);
  if (aliases.some((alias) => alias === normalizedInput)) return 120;
  if (aliases.some((alias) => alias.length >= 4 && normalizedInput.includes(alias) && alias.split(' ').length >= 3)) return 112;
  if (aliases.some((alias) => alias.length >= 4 && normalizedInput.includes(alias))) return 108;
  if (aliases.some((alias) => alias.length >= 4 && alias.includes(normalizedInput))) return 95;

  const inputTokens = normalizedInput.split(' ').filter((token) => token.length >= 2);
  const bestTokenRatio = aliases.reduce((best, alias) => {
    const aliasTokens = new Set(alias.split(' ').filter((token) => token.length >= 2));
    if (!inputTokens.length || !aliasTokens.size) return best;

    const matched = inputTokens.filter((token) => aliasTokens.has(token)).length;
    return Math.max(best, matched / inputTokens.length);
  }, 0);

  if (bestTokenRatio === 1 && inputTokens.length >= 2) return 85;
  if (bestTokenRatio >= 0.75 && inputTokens.length >= 2) return 70;

  return 0;
}

function getMatchedOptionValue(option: { value: string; label: string }, useSellerDisplayValue = false): string {
  return useSellerDisplayValue ? getSellerOptionDisplayValue(option) : option.value;
}

function findOptionValue(
  rawValue: unknown,
  options?: { value: string; label: string }[],
  useSellerDisplayValue = false
): string {
  if (rawValue === undefined || rawValue === null) return '';

  const value = String(rawValue).trim();
  if (!value) return '';

  if (!options || options.length === 0) return useSellerDisplayValue ? normalizeSellerValue(value) : value;

  const directMatch = options.find((opt) => String(opt.value) === value || String(opt.label) === value);
  if (directMatch) return getMatchedOptionValue(directMatch, useSellerDisplayValue);

  const scoredMatches = options
    .map((opt) => ({ option: opt, score: getOptionMatchScore(value, opt) }))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scoredMatches.length && scoredMatches[0].score >= 95 && scoredMatches[0].score > (scoredMatches[1]?.score ?? 0)) {
    return getMatchedOptionValue(scoredMatches[0].option, useSellerDisplayValue);
  }

  const startsWithNumber = value.match(/^(\d+)/);
  if (startsWithNumber) {
    const numberMatch = options.find((opt) => opt.value === startsWithNumber[1]);
    if (numberMatch) return getMatchedOptionValue(numberMatch, useSellerDisplayValue);
  }

  return useSellerDisplayValue ? normalizeSellerValue(value) : value;
}

function findOptionValues(
  rawValue: unknown,
  options?: { value: string; label: string }[],
  useSellerDisplayValue = false
): string[] {
  const rawList = splitAgentListValue(rawValue);
  const values: string[] = [];
  const optionValues = new Set((options ?? []).map((option) => getMatchedOptionValue(option, useSellerDisplayValue)));

  rawList.forEach((item) => {
    const matched = findOptionValue(item, options, useSellerDisplayValue);
    if (matched && (!options?.length || optionValues.has(matched))) values.push(matched);
  });

  return Array.from(new Set(values));
}

function normalizeAgentValues(values: FormValues, template: TemplateDef): FormValues {
  const fieldMap = new Map(
    template.sections.flatMap((section) =>
      section.fields.map((field) => [field.key, field])
    )
  );

  const normalized: FormValues = {};

  Object.entries(values).forEach(([key, rawValue]) => {
    const field = fieldMap.get(key) ?? fieldMap.get(baseFieldKey(key));

    if (!field) {
      normalized[key] = rawValue;
      return;
    }

    if (field.type === 'select') {
      normalized[key] = findOptionValue(rawValue, field.options);
      return;
    }

    if (field.type === 'multiselect') {
      normalized[key] = findOptionValues(rawValue, field.options, isSellerFieldKey(key) || isSellerFieldKey(field.key));

      return;
    }

    if (Array.isArray(rawValue)) {
      normalized[key] = rawValue.map((item) => String(item).trim()).filter(Boolean);
      return;
    }

    normalized[key] = String(rawValue ?? '').trim();
  });

  return normalized;
}

  const handleFillFromAgent = (newValues: FormValues) => {
    const normalizedValues = normalizeAgentValues(newValues, template);

    setValues((prev) => {
      const next = {
        ...prev,
        ...normalizedValues,
      };

      syncChildCardValues(next);

      return next;
    });

    setSubmitted(false);
    setSavedId(undefined);
    setSaveError(null);
    setValidationErrors((prev) => {
      const next = { ...prev };
      Object.keys(normalizedValues).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const handleExportXLSX = () => {
    const exporter = XLSX_EXPORTERS[template.id];
    if (exporter) exporter(normalizeSellerFieldsInValues(values, sellerOptions));
  };

  const hasXlsxExport = template.id in XLSX_EXPORTERS;
  const pendingMissingCount = pendingProductConfirmation?.issues.reduce(
    (total, issue) => total + issue.missing.length,
    0
  ) ?? 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          pb: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: template.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem' }}>
              {template.number}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {template.name}
            </Typography>
            <Chip
              label={template.mechanicsBadge}
              size="small"
              sx={{
                mt: 0.5,
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: `${template.color}15`,
                color: template.color,
                letterSpacing: '0.3px',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={allExpanded ? 'Todos os campos já estão abertos' : 'Expandir todas as seções'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<UnfoldMoreIcon fontSize="small" />}
                onClick={handleExpandAll}
                disabled={allExpanded}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Expandir todos
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={allCollapsed ? 'Todos os campos já estão fechados' : 'Recolher todas as seções'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<UnfoldLessIcon fontSize="small" />}
                onClick={handleCollapseAll}
                disabled={allCollapsed}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Recolher todos
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
            onClick={handleCopyJSON}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Copiar JSON
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={handleClearForm}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Limpar
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={onReset}
          >
            Trocar template
          </Button>
        </Box>
      </Box>

      {reuseSourceId !== undefined && reuseSourceId !== null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Reaproveitando dados do briefing {reuseSourceId}. Ao salvar, será criado um novo briefing.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {renderItems.map((item) => {
          if (item.kind === 'section') {
            const { section, sectionIndex, displayIndex } = item.slot;

            return (
              <FormSection
                key={item.key}
                section={section}
                index={displayIndex}
                values={values}
                onChange={handleChange}
                accentColor={template.color}
                fieldErrors={fieldErrors}
                expanded={expandedSections[sectionIndex] ?? true}
                onToggle={() => handleToggleSection(sectionIndex)}
              />
            );
          }

          return (
            <Box
              key={item.key}
              sx={{
                border: '1px solid',
                borderColor: item.color,
                borderRadius: 1,
                overflow: 'hidden',
                mb: 1.5,
                bgcolor: `${item.color}08`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  bgcolor: `${item.color}16`,
                  borderBottom: '1px solid',
                  borderColor: `${item.color}40`,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: item.color,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      flexShrink: 0,
                    }}
                  >
                    {item.childNumber}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: item.color, lineHeight: 1.2 }}>
                      Card filho {item.childNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Blocos internos deste card
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  size="small"
                  label={`${item.slots.length} bloco${item.slots.length !== 1 ? 's' : ''}`}
                  sx={{
                    height: 22,
                    bgcolor: '#fff',
                    color: item.color,
                    border: '1px solid',
                    borderColor: `${item.color}55`,
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Box sx={{ p: 1.25, pb: 0.25 }}>
                {item.slots.map(({ section, sectionIndex, displayIndex }) => (
                  <FormSection
                    key={`child-${item.childNumber}-${section.title}-${sectionIndex}`}
                    section={section}
                    index={displayIndex}
                    values={values}
                    onChange={handleChange}
                    accentColor={item.color}
                    fieldErrors={fieldErrors}
                    expanded={expandedSections[sectionIndex] ?? true}
                    onToggle={() => handleToggleSection(sectionIndex)}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ my: 3 }} />

      {submitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Briefing salvo com sucesso!{savedId ? ` ID: ${savedId}.` : ''} Campos preenchidos: {Object.keys(values).filter((k) => values[k] && values[k] !== '').length}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClearForm}
          sx={{ color: 'text.secondary', borderColor: 'divider' }}
        >
          Limpar formulário
        </Button>
        {/*hasXlsxExport && (
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportXLSX}
            sx={{
              borderColor: template.color,
              color: template.color,
              '&:hover': {
                borderColor: template.color,
                bgcolor: `${template.color}10`,
              },
            }}
          >
            Exportar XLSX
          </Button>
        )*/}
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSubmit}
          disabled={saving}
          sx={{ bgcolor: template.color, '&:hover': { bgcolor: template.color, filter: 'brightness(0.9)' } }}
        >
          {saving ? 'Salvando...' : 'Salvar briefing'}
        </Button>
      </Box>

      <AgentChatWidget
  templateId={template.id}
  templateName={template.name}
  currentValues={values}
  onFillForm={handleFillFromAgent}
/>

      <Dialog
        open={Boolean(pendingProductConfirmation)}
        onClose={handleCancelProductConfirmation}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
          <WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 24 }} />
          <Typography variant="subtitle1" fontWeight={800}>
            SKU/EAN nao encontrado
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {pendingMissingCount} codigo(s) nao foram encontrados e nao serao salvos. Deseja continuar salvando o briefing sem eles?
          </Typography>

          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Os campos abaixo serao salvos apenas com os SKUs/EANs validos.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 280, overflowY: 'auto' }}>
            {pendingProductConfirmation?.issues.map((issue) => (
              <Box
                key={issue.fieldKey}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1.25,
                  py: 1,
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="caption" fontWeight={800} color="text.primary" display="block">
                  {issue.label}
                </Typography>
                <Typography variant="caption" color="error.main" fontWeight={700} display="block" sx={{ mt: 0.5 }}>
                  Removidos: {issue.missing.join(', ')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  Restantes: {issue.remaining.length > 0 ? issue.remaining.join(', ') : 'nenhum codigo valido'}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancelProductConfirmation}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Voltar e corrigir
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmSaveWithoutInvalidProducts}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Salvar sem estes codigos
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
